"use client";
import { Icons, Videos } from "@assets/assets";
import Image from "next/image";
import Link from "next/link";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { TECH_CATEGORIES } from "./data";
import TechnologyHeader from "./TechnologyHeader";
import { whenIdle } from "@/lib/whenIdle";

// Lazy load GSAP
const loadGSAP = () => import("gsap").then((mod) => mod.default);

type TechnologyItem = {
  name: string;
  icon: any;
  link: string;
};

type TechnologyCategory = {
  key: string;
  label: string;
  technologies: TechnologyItem[];
};

export interface TechnologyProps {
  categories?: TechnologyCategory[];
  title?: React.ReactNode;
  description?: React.ReactNode;
}

export default function Technology({
  categories = TECH_CATEGORIES,
  title,
  description,
}: TechnologyProps) {
  const sectionRef1 = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  // const [isSplineLoaded, setIsSplineLoaded] = useState(false);
  const [hasSplineLoadedOnce, setHasSplineLoadedOnce] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const lineRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);

  const [translateX, setTranslateX] = useState(0);
  const tabContentRef = useRef<HTMLDivElement | null>(null);
  const [tabOffsets, setTabOffsets] = useState<number[]>([]);
  const [lineX, setLineX] = useState(0);
  const [lineWidth, setLineWidth] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      {
        threshold: 0.2,
      },
    );

    if (sectionRef1.current) {
      observer.observe(sectionRef1.current);
    }
  }, []);

  // autoplay is only honoured while the resource is being selected, so flipping
  // the attribute after mount never starts playback — with preload="none" there
  // is nothing loaded to play. Drive it from the ref instead: the first play()
  // is also what fetches the file, so the video still costs nothing until the
  // section scrolls into view.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (inView) {
      // Rejects if the browser blocks autoplay; it's muted so this is unlikely,
      // and a decorative loop is not worth surfacing an error for.
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [inView]);

  const [activeTab, setActiveTab] = useState(categories[0].key);

  const activeIndex = categories.findIndex((cat) => cat.key === activeTab);
  const activeCategory = categories.find((cat) => cat.key === activeTab);

  const handlePrev = () => {
    if (activeIndex > 0) {
      const prevIndex = activeIndex - 1;
      const prevKey = categories[prevIndex].key;
      setActiveTab(prevKey);
      scrollTabIntoViewIfNeeded(prevIndex);
    }
  };

  const handleNext = () => {
    if (activeIndex < categories.length - 1) {
      const nextIndex = activeIndex + 1;
      const nextKey = categories[nextIndex].key;
      setActiveTab(nextKey);
      scrollTabIntoViewIfNeeded(nextIndex);
    }
  };

  const computeTabOffsets = useCallback(() => {
    requestAnimationFrame(() => {
      const container = sliderRef.current;
      const track = trackRef.current;
      if (!container || !track) return;

      // Perform all READS first
      const containerWidth = container.clientWidth;
      const trackWidth = track.scrollWidth;
      const navRect = navRef.current?.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const overlap = navRect
        ? Math.max(0, containerRect.right - navRect.left)
        : 0;
      const visibleWidth = Math.max(0, containerWidth - overlap);
      const minX = Math.min(0, visibleWidth - trackWidth);

      // Collect data into a single object to update state once
      const newLayoutData = {
        offsets: [] as number[],
        activeLineX: 0,
        activeLineWidth: 0,
        activeTranslateX: 0,
      };

      newLayoutData.offsets = categories.map((_, index) => {
        const tabEl = tabRefs.current[index];
        if (!tabEl) return 0;

        const tabLeft = tabEl.offsetLeft;
        const tabWidth = tabEl.offsetWidth;
        const tabRight = tabLeft + tabWidth;

        // Logic for indicator line
        if (categories[index].key === activeTab) {
          newLayoutData.activeLineX = tabLeft;
          newLayoutData.activeLineWidth = tabWidth;
        }

        let x = -tabLeft;
        if (tabRight > -x + visibleWidth) {
          x = -(tabRight - visibleWidth);
        }
        return Math.max(minX, Math.min(0, x));
      });

      // Final "WRITE" - Batch the updates
      setTabOffsets(newLayoutData.offsets);
      setLineX(newLayoutData.activeLineX);
      setLineWidth(newLayoutData.activeLineWidth);
      setTranslateX(newLayoutData.offsets[activeIndex] || 0);
    });
  }, [categories, activeTab, activeIndex]);

  const scrollTabIntoViewIfNeeded = (index: number) => {
    if (typeof tabOffsets[index] === "number") {
      setTranslateX(tabOffsets[index]);
    } else {
      computeTabOffsets();
    }
  };

  // Once Spline has loaded, prevent it from being unloaded again
  useEffect(() => {
    if (inView && !hasSplineLoadedOnce) {
      // setIsSplineLoaded(true);
      setHasSplineLoadedOnce(true);
    }
  }, [inView, hasSplineLoadedOnce]);

  useEffect(() => {
    if (!tabContentRef.current) return;

    let cancelled = false;
    // Idle-deferred: on first mount this section is below the fold, so the
    // entrance animation must not add GSAP work to the hydration window.
    // On tab clicks the browser is idle almost immediately — no visible delay.
    const cancelIdle = whenIdle(() => {
      if (cancelled || !tabContentRef.current) return;
      loadGSAP().then((gsap) => {
        if (cancelled || !tabContentRef.current) return;
        gsap.fromTo(
          tabContentRef.current.querySelectorAll(".tab-content-box"),
          {
            opacity: 0.01,
            y: 60,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: "power2.out",
          },
        );
      });
    });

    return () => {
      cancelled = true;
      cancelIdle();
    };
  }, [activeTab]);

  useEffect(() => {
    let timeoutId: any;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(computeTabOffsets, 100); // Wait for resize to finish
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [computeTabOffsets]);

  useEffect(() => {
    if (tabOffsets.length && activeIndex >= 0) {
      setTranslateX(tabOffsets[activeIndex]);
      const activeEl = tabRefs.current[activeIndex];
      if (activeEl) {
        setLineX(activeEl.offsetLeft);
        setLineWidth(activeEl.offsetWidth);
      }
    }
  }, [activeIndex, tabOffsets]);

  // Was useLayoutEffect: measuring tab offsets (offsetLeft/getBoundingClientRect)
  // during hydration forces a synchronous reflow. The tab indicator is below
  // the fold, so idle-deferring the measurement is invisible to the user.
  useEffect(() => {
    const cancelIdle = whenIdle(computeTabOffsets);
    return cancelIdle;
  }, [computeTabOffsets]);

  return (
    <section
      className="bg-[#fff] w-full relative technology-section overflow-hidden"
      ref={sectionRef1}
    >
      <div className="max-w-[5000px] relative z-[2] mx-auto w-[calc(100%_-_10%)] py-[50px] lg:py-[150px]">
        <TechnologyHeader title={title} description={description} />
        <div className="technology-tab-area pt-[40px] gap-[10px] lg:gap-0 lg:pt-[100px] flex justify-between">
          <div
            className="technology-slider-container overflow-hidden relative w-full"
            ref={sliderRef}
          >
            <div
              ref={trackRef}
              className="flex transition-transform duration-500 ease-in-out relative"
              style={{ transform: `translateX(${translateX}px)` }}
            >
              {categories?.map((category, index) => {
                const isActive = category.key === activeTab;
                return (
                  <div
                    key={category.key}
                    className="tab-container flex flex-col gap-0 cursor-pointer w-[120px] min-w-[100%] lg:min-w-[var(--technology-tab-width)] relative"
                    onClick={() => {
                      setActiveTab(category.key);
                      scrollTabIntoViewIfNeeded(index);
                    }}
                    ref={(el) => {
                      tabRefs.current[index] = el;
                    }}
                  >
                    <div
                      className={`number pb-6 ${isActive ? "text-[#225BD4]" : "text-[#636364]"} border-b border-[#ECF0F6]`}
                    >
                      <span
                        className={`text-[length:var(--hero-para)] font-[${isActive ? "700" : "400"}]`}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div
                      className={`pt-[35px] pr-[15px] tab-name 
                    text-[length:var(--primarybtn)] 
                    leading-tight 
                    break-words 
                    whitespace-normal 
                    text-start
                    ${isActive ? "text-[#225BD4] font-[700]" : "text-[#636364]"}`}
                    >
                      {category.label}
                    </div>
                  </div>
                );
              })}

              {/* Line indicator outside the map */}
              <div
                ref={lineRef}
                className="line absolute h-[4px] bg-[#225BD4] top-[clamp(3.625rem,1.3644rem+1.8838vw,4.375rem)] transition-transform duration-300 ease-in-out"
                style={{
                  width: `${lineWidth}px`,
                  transform: `translateX(${lineX}px)`,
                }}
              ></div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div
            ref={navRef}
            className="technology-tab-switch flex gap-[10px] w-[max-content] mt-4"
          >
            <button
              onClick={handlePrev}
              disabled={activeIndex === 0}
              aria-label="Previous slide"
              className={`cursor-pointer ${activeIndex === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Image
                src={Icons?.slideLeft as any}
                alt="Arrow left"
                width={40}
                height={40}
                className="size-[40px]"
                sizes="(max-width: 1024px) 100vw, 50vw"
                loading="lazy"
              />
            </button>

            <button
              onClick={handleNext}
              disabled={activeIndex === categories.length - 1}
              aria-label="Next slide"
              className={`cursor-pointer ${activeIndex === categories.length - 1 ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Image
                src={Icons?.slideRight as any}
                alt="Arrow right"
                width={40}
                height={40}
                className="size-[40px]"
                sizes="(max-width: 1024px) 100vw, 50vw"
                loading="lazy"
              />
            </button>
          </div>
        </div>

        <div
          ref={tabContentRef}
          className="technology-tab-content lg:max-w-[calc(var(--technology-tab-width)*6)] grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-[0] max-w-[1372px] w-full mt-[80px]"
        >
          {activeCategory?.technologies?.map((tech, index) => {
            const Wrapper: any = tech?.link ? Link : "div";
            return (
              <Wrapper
                key={tech.name + index}
                {...(tech?.link ? { href: tech.link } : {})}
                className="tab-content-box relative aspect-[1/1] flex items-center justify-center border flex-col gap-[10px] border-[#ECF0F6]"
              >
                {tech.icon ? (
                  <Image
                    src={tech.icon as any}
                    alt={tech.name}
                    width={60} // or actual original width, e.g. 512 if it's a larger PNG
                    height={60} // same for height – keeps square aspect
                    className="size-[60px]"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    loading="lazy"
                  />
                ) : null}
                <p className="text-center capitalize text-[#00040b] text-[1.15rem] font-medium leading-tight">
                  {tech.name}
                </p>
              </Wrapper>
            );
          })}
        </div>
      </div>
      <div className="absolute bottom-[-120px] right-2 transform translate-x-1/7 size-[700px] scale-[0.7] z-[1] hidden min-[1560px]:block">
        <video
          ref={videoRef}
          key="hero-video"
          loop
          muted
          playsInline
          preload="none"
          className="mobile-toggle-video z-20 h-full object-cover"
        >
          <source src={Videos.modelVideo3} type="video/mp4" />
          <track
            kind="captions"
            srcLang="en"
            label="English"
            src={`${process.env.NEXT_PUBLIC_BASE_PATH || ""}/captions/decorative.vtt`}
          />
          Your browser does not support the video tag.
        </video>
      </div>
    </section>
  );
}
